import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { submitAppointment } from "@/services/leadService";
import { trackEvent } from "@/services/analytics";
import { formatLongDate, formatMonth, getDemoAvailability, readStoredLead, storeAppointment, toDateKey } from "@/services/bookingService";

function calendarDays(month, availability) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const total = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const days = Array(first.getDay()).fill(null);
  for (let day = 1; day <= total; day += 1) days.push(new Date(month.getFullYear(), month.getMonth(), day));
  return days.map((date) => ({ date, key: date ? toDateKey(date) : "", available: date ? Boolean(availability[toDateKey(date)]) : false }));
}

function slug(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

function CalendarPanel({ month, days, selectedDate, setMonth, chooseDate }) {
  return <section className="booking-step-panel booking-calendar-panel" data-testid="booking-calendar-step"><div className="booking-panel-heading"><span className="booking-step">01 / Choose a date</span><div className="booking-month-controls"><button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label="Previous month" data-testid="booking-month-previous"><ChevronLeft size={17} aria-hidden="true" /></button><strong data-testid="booking-month-label">{formatMonth(month)}</strong><button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label="Next month" data-testid="booking-month-next"><ChevronRight size={17} aria-hidden="true" /></button></div></div><div className="calendar-weekdays" aria-hidden="true">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <span key={day}>{day}</span>)}</div><div className="calendar-grid">{days.map(({ date, key, available }, index) => <button key={key || `empty-${index}`} type="button" disabled={!date || !available} className={`${key && selectedDate === key ? "is-selected" : ""} ${available ? "is-available" : "is-disabled"}`} onClick={() => chooseDate(key)} aria-label={date ? `${formatLongDate(key)}${available ? " available" : " unavailable"}` : "Empty calendar day"} data-testid={`booking-date-${key || `empty-${index}`}`}>{date?.getDate()}</button>)}</div><span className="booking-note">Select an available date to see first-lesson times.</span></section>;
}

function TimePanel({ selectedDate, times, selectedTime, chooseTime, onBack }) {
  return <section className="booking-step-panel booking-time-panel" data-testid="booking-time-step"><button type="button" className="booking-back-link" onClick={onBack} data-testid="booking-back-to-calendar">← Back to calendar</button><span className="booking-step">02 / Choose a time</span><h2>{formatLongDate(selectedDate)}</h2><div className="booking-times">{times.map((time) => <button type="button" key={time} className={selectedTime === time ? "is-selected" : ""} onClick={() => chooseTime(time)} data-testid={`booking-time-${slug(time)}`}>{time}</button>)}</div></section>;
}

function ContactPanel({ selectedDate, selectedTime, contact, updateContact, submit, error, submitting, onBack }) {
  return <section className="booking-step-panel booking-contact-panel" data-testid="booking-contact-step"><button type="button" className="booking-back-link" onClick={onBack} data-testid="booking-back-to-times">← Back to times</button><form className="booking-contact" onSubmit={submit} data-testid="booking-contact-form"><span className="booking-step">03 / Confirm your details</span><div className="booking-summary"><strong>Your first lesson</strong><span>{formatLongDate(selectedDate)} at {selectedTime}</span></div><label htmlFor="booking-name">Name</label><input id="booking-name" type="text" value={contact.name} onChange={updateContact("name")} autoComplete="name" required data-testid="booking-name-input" /><label htmlFor="booking-email">Email</label><input id="booking-email" type="email" value={contact.email} onChange={updateContact("email")} autoComplete="email" required data-testid="booking-email-input" /><label htmlFor="booking-phone">Phone</label><input id="booking-phone" type="tel" inputMode="tel" value={contact.phone} onChange={updateContact("phone")} autoComplete="tel" required data-testid="booking-phone-input" />{error && <p className="booking-error" role="alert" data-testid="booking-error">{error}</p>}<button type="submit" className="button button-accent booking-confirm" disabled={submitting} data-testid="confirm-first-lesson-button">{submitting ? "Saving your time" : "Book Now"}<ArrowRight size={16} aria-hidden="true" /></button></form></section>;
}

export function BookingCalendar({ onProgress = () => {} }) {
  const navigate = useNavigate();
  const availability = useMemo(() => getDemoAvailability(), []);
  const lead = useMemo(() => readStoredLead(), []);
  const firstAvailable = Object.keys(availability)[0];
  const [month, setMonth] = useState(() => new Date(`${firstAvailable}T12:00:00`));
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [contact, setContact] = useState({ name: lead?.name || "", email: lead?.email || "", phone: lead?.phone || "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const bookingStartedRef = useRef("");
  const days = calendarDays(month, availability);
  const times = selectedDate ? availability[selectedDate] || [] : [];
  const step = selectedTime ? "contact" : selectedDate ? "time" : "calendar";

  useEffect(() => {
    if (!selectedTime || bookingStartedRef.current === `${selectedDate}-${selectedTime}`) return;
    bookingStartedRef.current = `${selectedDate}-${selectedTime}`;
    trackEvent("appointment_booking_started", { date: selectedDate, time: selectedTime });
  }, [selectedDate, selectedTime]);

  const chooseDate = (dateKey) => {
    setSelectedDate(dateKey);
    setSelectedTime("");
    setError("");
    trackEvent("appointment_date_selected", { date: dateKey });
  };

  const chooseTime = (time) => {
    setSelectedTime(time);
    setError("");
    onProgress(90);
    trackEvent("appointment_time_selected", { date: selectedDate, time });
  };

  const backToCalendar = () => {
    setSelectedDate("");
    setSelectedTime("");
    setError("");
    onProgress(80);
  };

  const backToTimes = () => {
    setSelectedTime("");
    setError("");
    onProgress(90);
  };

  const updateContact = (field) => (event) => setContact((current) => ({ ...current, [field]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!selectedDate || !selectedTime || !contact.name.trim() || !contact.email.trim() || !contact.phone.trim()) {
      setError("Choose a date, a time, and complete your contact details.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const result = await submitAppointment({ lead_id: lead?.leadId || "direct-demo", date: selectedDate, time: selectedTime, ...contact });
      const appointment = { ...result, ...contact, date: selectedDate, time: selectedTime, dateLabel: formatLongDate(selectedDate) };
      storeAppointment(appointment);
      trackEvent("appointment_confirmed", { date: selectedDate, time: selectedTime });
      onProgress(100);
      navigate("/confirmation");
    } catch {
      setError("We could not save that time just yet. Please choose it again.");
    } finally {
      setSubmitting(false);
    }
  };

  return <div className={`booking-widget booking-widget-step booking-widget-${step}`} data-testid="booking-calendar">{step === "calendar" && <CalendarPanel month={month} days={days} selectedDate={selectedDate} setMonth={setMonth} chooseDate={chooseDate} />}{step === "time" && <TimePanel selectedDate={selectedDate} times={times} selectedTime={selectedTime} chooseTime={chooseTime} onBack={backToCalendar} />}{step === "contact" && <ContactPanel selectedDate={selectedDate} selectedTime={selectedTime} contact={contact} updateContact={updateContact} submit={submit} error={error} submitting={submitting} onBack={backToTimes} />}</div>;
}
